import { formatPhone } from '@fundline/shared/phone';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  Building2,
  Ellipsis,
  type LucideIcon,
  Megaphone,
  Menu,
  MessageCircle,
  Settings,
  Store,
  X,
} from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Avatar } from '../components/ui/Avatar.tsx';
import { Logo } from '../components/ui/Logo.tsx';
import { ThemeSwitch } from '../components/ui/ThemeToggle.tsx';
import { useProfileQuery } from '../features/account/use-profile.ts';
import { NoAccessPage } from '../features/auth/NoAccessPage.tsx';
import { useInboxQuery } from '../features/inbox/use-inbox.ts';
import { NotificationBell } from '../features/notifications/NotificationBell.tsx';
import { useNumbersQuery } from '../features/settings/use-numbers.ts';
import { useWorkspaceQuery } from '../features/settings/use-workspace.ts';
import { useAuth } from '../lib/auth.tsx';
import { useInboxRealtime } from '../lib/use-inbox-realtime.ts';
import { NAV_QUERY, useMediaQuery } from '../lib/use-media-query.ts';

type NavTo = '/inbox' | '/campaigns' | '/merchants' | '/platform';

interface NavItem {
  to: NavTo;
  label: string;
  Icon: LucideIcon;
}

/**
 * Main sections in the order of brand/layout.md. Only built sections are listed; Pipeline, Calls,
 * Sequences, Calendar and Reports join as their milestones ship. Campaigns (the layout's "Blasts" slot,
 * M9) are for admins only.
 */
function navItems(workspace: boolean, platformOwner: boolean, isAdmin: boolean): NavItem[] {
  const items: NavItem[] = [];
  if (workspace) {
    items.push({ to: '/inbox', label: 'Inbox', Icon: MessageCircle });
    if (isAdmin) items.push({ to: '/campaigns', label: 'Campaigns', Icon: Megaphone });
    items.push({ to: '/merchants', label: 'Merchants', Icon: Store });
  }
  if (platformOwner) items.push({ to: '/platform', label: 'Platform', Icon: Building2 });
  return items;
}

const ICON = { size: 20, strokeWidth: 1.75, 'aria-hidden': true } as const;

const NAV_LINK =
  'callout flex min-h-11 items-center gap-3 rounded-md px-3 font-semibold text-ink hover:bg-surface-raised focus-visible:outline-2 focus-visible:outline-focus-ring data-[status=active]:bg-brand-tint data-[status=active]:text-brand-on-tint';

function WorkspaceName({ workspaceId }: { workspaceId: string }) {
  const { data } = useWorkspaceQuery(workspaceId);
  return <span data-testid="workspace-name">{data?.name ?? ' '}</span>;
}

/** Live inbox connection for members; a badge when it has been down for 10 seconds. */
function LiveStatus({
  workspaceId,
  userId,
  isAdmin,
}: {
  workspaceId: string;
  userId: string;
  isAdmin: boolean;
}) {
  const { reconnecting } = useInboxRealtime(workspaceId, userId, isAdmin);
  if (!reconnecting) return null;
  return (
    <div
      role="status"
      className="footnote fixed top-2 left-1/2 z-30 -translate-x-1/2 rounded-pill bg-warning-tint px-3 py-1 font-semibold text-warning shadow-card"
      data-testid="reconnecting"
    >
      Reconnecting… The inbox refreshes every 10 seconds meanwhile.
    </div>
  );
}

/** Conversations with something unread: the Iris count on Inbox (first page; "50+" beyond it). */
function UnreadCount({ workspaceId }: { workspaceId: string }) {
  const unread = useInboxQuery(workspaceId, { view: 'unread', rep: null });
  const count = unread.data?.pages[0]?.length ?? 0;
  if (count === 0) return null;
  const label = unread.hasNextPage ? `${count}+` : String(count);
  return (
    <span
      className="caption ml-auto rounded-pill bg-brand px-2 py-0.5 text-on-brand"
      aria-label={`${label} unread`}
      data-testid="nav-unread"
    >
      {label}
    </span>
  );
}

/** Above Settings: the texting line and whether it is connected (brand/layout.md "Navigation"). */
function LineStatus({ workspaceId }: { workspaceId: string }) {
  const numbers = useNumbersQuery(workspaceId);
  const active = (numbers.data ?? []).filter((n) => n.status === 'active');
  if (!numbers.data) return null;
  const first = active[0];
  return (
    <div
      className="rounded-md bg-surface-raised px-3 py-2 ring-1 ring-hairline"
      data-testid="line-status"
    >
      <p className="footnote text-ink-muted">
        {active.length > 1 ? `${active.length} texting numbers` : 'Texting line'}
      </p>
      <p className="callout flex items-center gap-2 text-ink">
        <span
          className={`size-2 shrink-0 rounded-pill ${first ? 'bg-success' : 'bg-control-border'}`}
          aria-hidden
        />
        {first ? (
          <>
            <span className="fl-num truncate">{formatPhone(first.e164)}</span>
            <span className="sr-only">connected</span>
          </>
        ) : (
          'No number yet'
        )}
      </p>
    </div>
  );
}

function UserAvatar({ userId, email }: { userId: string; email: string | null }) {
  const profile = useProfileQuery(userId);
  return <Avatar name={profile.data?.full_name || email} size="sm" brand />;
}

function UserRow({
  userId,
  email,
  role,
}: {
  userId: string;
  email: string | null;
  role: string | null;
}) {
  const profile = useProfileQuery(userId);
  const name = profile.data?.full_name || email || 'Account';
  return (
    <div className="flex items-center gap-2">
      <Link
        to="/account"
        className="flex min-w-0 flex-1 items-center gap-2 rounded-md p-1 hover:bg-surface-raised focus-visible:outline-2 focus-visible:outline-focus-ring"
        aria-label="Account"
      >
        <Avatar name={profile.data?.full_name || email} size="sm" brand />
        <span className="min-w-0">
          <span className="callout block truncate font-semibold text-ink" title={email ?? ''}>
            {name}
          </span>
          <span className="footnote block truncate text-ink-muted">
            {role === 'admin' ? 'Admin' : role === 'rep' ? 'Rep' : 'Platform owner'}
          </span>
        </span>
      </Link>
      <ThemeSwitch />
    </div>
  );
}

const RAIL_LINK =
  'relative flex size-12 items-center justify-center rounded-md text-ink hover:bg-surface-raised focus-visible:outline-2 focus-visible:outline-focus-ring data-[status=active]:bg-brand-tint data-[status=active]:text-brand-on-tint';

interface ShellProps {
  claims: NonNullable<ReturnType<typeof useAuth>['claims']>;
  items: NavItem[];
  isAdmin: boolean;
  inSettings: boolean;
  contextLabel: ReactNode;
}

/** The full 232px navigation: in the sidebar from 1072px, and in the drawer opened from the rail. */
function FullNav({
  claims,
  items,
  isAdmin,
  inSettings,
  contextLabel,
  closeButton,
}: ShellProps & { closeButton?: ReactNode }) {
  return (
    <>
      <div className="flex items-start justify-between gap-1 px-1 pt-2">
        <div className="min-w-0 shrink-0">
          <Logo width={120} />
          <p className="footnote mt-2 truncate text-ink-muted">{contextLabel}</p>
        </div>
        <div className="flex shrink-0 items-center">
          {claims.workspaceId ? <NotificationBell workspaceId={claims.workspaceId} /> : null}
          {closeButton}
        </div>
      </div>
      <nav
        className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto"
        aria-label="Main navigation"
      >
        {items.map(({ to, label, Icon }) => (
          <Link key={to} to={to} className={NAV_LINK}>
            <Icon {...ICON} />
            {label}
            {to === '/inbox' && claims.workspaceId ? (
              <UnreadCount workspaceId={claims.workspaceId} />
            ) : null}
          </Link>
        ))}
        <div className="mt-auto flex flex-col gap-1 pt-4">
          {claims.workspaceId ? <LineStatus workspaceId={claims.workspaceId} /> : null}
          {claims.workspaceId && isAdmin ? (
            <Link
              to="/settings/workspace"
              className={`${NAV_LINK} ${inSettings ? 'bg-brand-tint text-brand-on-tint' : ''}`}
            >
              <Settings {...ICON} />
              Settings
            </Link>
          ) : null}
        </div>
      </nav>
      <div className="border-t border-hairline pt-3">
        <UserRow userId={claims.userId} email={claims.email} role={claims.role} />
      </div>
    </>
  );
}

/**
 * The 72px icon rail (768–1071px): the menu button opens the full navigation as a drawer; every icon
 * keeps its section name as its accessible name.
 */
function RailNav({
  claims,
  items,
  isAdmin,
  inSettings,
  onOpenMenu,
}: ShellProps & { onOpenMenu: () => void }) {
  return (
    <>
      <button
        type="button"
        className={RAIL_LINK}
        aria-label="Open navigation"
        aria-haspopup="dialog"
        onClick={onOpenMenu}
        data-testid="nav-menu"
      >
        <Menu {...ICON} />
      </button>
      <nav className="flex min-h-0 flex-1 flex-col items-center gap-1" aria-label="Main navigation">
        {items.map(({ to, label, Icon }) => (
          <Link key={to} to={to} className={RAIL_LINK} aria-label={label} title={label}>
            <Icon {...ICON} />
            {to === '/inbox' && claims.workspaceId ? (
              <RailUnreadDot workspaceId={claims.workspaceId} />
            ) : null}
          </Link>
        ))}
        <div className="mt-auto flex flex-col items-center gap-1 pt-4">
          {claims.workspaceId && isAdmin ? (
            <Link
              to="/settings/workspace"
              className={`${RAIL_LINK} ${inSettings ? 'bg-brand-tint text-brand-on-tint' : ''}`}
              aria-label="Settings"
              title="Settings"
            >
              <Settings {...ICON} />
            </Link>
          ) : null}
        </div>
      </nav>
      {claims.workspaceId ? <NotificationBell workspaceId={claims.workspaceId} /> : null}
      <Link
        to="/account"
        className="rounded-pill focus-visible:outline-2 focus-visible:outline-focus-ring"
        aria-label="Account"
        title="Account"
      >
        <UserAvatar userId={claims.userId} email={claims.email} />
      </Link>
    </>
  );
}

/** Unread conversations on the rail's Inbox icon: an Iris dot (the count is in the drawer). */
function RailUnreadDot({ workspaceId }: { workspaceId: string }) {
  const unread = useInboxQuery(workspaceId, { view: 'unread', rep: null });
  const count = unread.data?.pages[0]?.length ?? 0;
  if (count === 0) return null;
  return (
    <span
      className="absolute top-2 right-2 size-2.5 rounded-pill bg-brand ring-2 ring-surface"
      aria-label={`${unread.hasNextPage ? `${count}+` : count} unread`}
    />
  );
}

/** The full navigation over the page, from the rail's menu button. Escape or the backdrop closes it. */
function NavDrawer({ onClose, ...props }: ShellProps & { onClose: () => void }) {
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40" role="presentation">
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        className="absolute inset-0 size-full cursor-default bg-ink opacity-30"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className="absolute inset-y-0 left-0 flex w-58 flex-col gap-4 border-r border-hairline bg-surface p-3 shadow-sheet"
        data-testid="nav-drawer"
      >
        <FullNav
          {...props}
          closeButton={
            <button
              ref={closeButton}
              type="button"
              className="fl-icon-btn text-ink-muted hover:text-ink focus-visible:outline-2 focus-visible:outline-focus-ring"
              aria-label="Close navigation"
              onClick={onClose}
            >
              <X size={20} strokeWidth={1.75} aria-hidden />
            </button>
          }
        />
      </div>
    </div>
  );
}

/**
 * The signed-in shell (brand/layout.md, ADR 0008 for the breakpoints). From 1072px: the 232px navigation
 * beside the page. 768–1071px: a 72px icon rail whose menu button opens the full navigation as a drawer.
 * Below 768px: a top bar and the bottom tab bar (Inbox · Merchants · More), both hidden inside a
 * conversation. The inbox fills the rest with its own columns. Navigation depends on the role in the
 * token; the database enforces it regardless.
 */
export function AppLayout() {
  const { claims } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const fullNav = useMediaQuery(NAV_QUERY);
  // The page the drawer was opened on: a new page (or a window wide enough for the sidebar) closes it.
  const [menuOpenOn, setMenuOpenOn] = useState<string | null>(null);
  const menuOpen = menuOpenOn === pathname && !fullNav;
  const closeMenu = useCallback(() => setMenuOpenOn(null), []);

  if (!claims || (!claims.workspaceId && !claims.platformOwner)) return <NoAccessPage />;

  const isAdmin = claims.role === 'admin';
  const items = navItems(claims.workspaceId !== null, claims.platformOwner, isAdmin);
  // Phones keep three tabs (Inbox · Merchants · More); Campaigns is under More there.
  const tabItems = items.filter((i) => i.to !== '/campaigns');
  const inInbox = pathname === '/inbox' || pathname.startsWith('/inbox/');
  const inConversation = pathname.startsWith('/inbox/');
  const inSettings = pathname.startsWith('/settings/') || pathname.startsWith('/dev/');
  const contextLabel = claims.workspaceId ? (
    <WorkspaceName workspaceId={claims.workspaceId} />
  ) : (
    <span data-testid="workspace-name">Platform (read-only)</span>
  );
  const shell: ShellProps = { claims, items, isAdmin, inSettings, contextLabel };

  return (
    <div className="flex min-h-full md:h-dvh md:overflow-hidden">
      {claims.workspaceId && claims.userId ? (
        <LiveStatus workspaceId={claims.workspaceId} userId={claims.userId} isAdmin={isAdmin} />
      ) : null}

      {/* CSS picks one (display:none takes the other out of the accessibility tree), so the switch is
          instant while a window is resized. */}
      <aside
        className="hidden w-58 shrink-0 flex-col gap-4 border-r border-hairline bg-surface p-3 nav:flex"
        aria-label="Main"
      >
        <FullNav {...shell} />
      </aside>
      <aside
        className="hidden w-18 shrink-0 flex-col items-center gap-2 border-r border-hairline bg-surface py-3 md:flex nav:hidden"
        aria-label="Main"
        data-testid="nav-rail"
      >
        <RailNav {...shell} onOpenMenu={() => setMenuOpenOn(pathname)} />
      </aside>
      {menuOpen ? <NavDrawer {...shell} onClose={closeMenu} /> : null}

      <div className="flex min-w-0 flex-1 flex-col">
        {inConversation ? null : (
          <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-hairline bg-bg px-4 py-2 md:hidden">
            <Link
              to="/account"
              className="flex min-h-11 min-w-0 items-center gap-2 focus-visible:outline-2 focus-visible:outline-focus-ring"
              aria-label="Account"
            >
              <UserAvatar userId={claims.userId} email={claims.email} />
              <span className="footnote truncate text-ink-muted">{contextLabel}</span>
            </Link>
            {claims.workspaceId ? <NotificationBell workspaceId={claims.workspaceId} /> : null}
          </header>
        )}

        <main
          className={
            inInbox
              ? 'flex min-h-0 min-w-0 flex-1 flex-col'
              : 'mx-auto w-full min-w-0 max-w-4xl flex-1 px-4 pt-5 pb-24 md:overflow-y-auto md:px-8 md:py-8'
          }
        >
          <Outlet />
        </main>

        {inConversation ? null : (
          <nav
            className="fixed inset-x-0 bottom-0 z-10 flex border-t border-hairline bg-bg pb-[env(safe-area-inset-bottom)] md:hidden"
            aria-label="Tabs"
          >
            {tabItems.map(({ to, label, Icon }) => (
              <Link
                key={to}
                to={to}
                className="caption flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-ink-muted focus-visible:outline-2 focus-visible:outline-focus-ring data-[status=active]:text-brand-text"
              >
                <Icon size={24} strokeWidth={1.75} aria-hidden />
                {label}
              </Link>
            ))}
            <Link
              to="/more"
              className="caption flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-ink-muted focus-visible:outline-2 focus-visible:outline-focus-ring data-[status=active]:text-brand-text"
            >
              <Ellipsis size={24} strokeWidth={1.75} aria-hidden />
              More
            </Link>
          </nav>
        )}
      </div>
    </div>
  );
}
